import { z } from 'zod';

import { MERCHANT_REPORT_STATUSES, STAKEHOLDER_ROLES } from '../consts';
import { EcosystemRecordSchema } from './report-schema';

/**
 * POS-specific ongoing monitoring change schema.
 * Uses category/reasoning/severity format (not the DF field/previousValue/etc. format)
 */
export const PointOfSaleOngoingMonitoringChangeSchema = z.object({
  category: z.string(),
  reasoning: z.string(),
  severity: z.enum(['low', 'medium', 'high']),
});

export type PointOfSaleOngoingMonitoringChange = z.infer<
  typeof PointOfSaleOngoingMonitoringChangeSchema
>;

/**
 * Point of Sale Report Status
 * This is used for both raw and transformed report shapes
 */
const PointOfSaleReportStatusEnum = z.enum([
  MERCHANT_REPORT_STATUSES[0],
  ...MERCHANT_REPORT_STATUSES.slice(1),
]);

export type PointOfSaleReportStatus = z.infer<typeof PointOfSaleReportStatusEnum>;

/**
 * Risk level for point of sale
 */
export const POINT_OF_SALE_RISK_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type PointOfSaleSynthesizerRiskLevel = (typeof POINT_OF_SALE_RISK_LEVELS)[number];

/**
 * Risk types for point of sale
 */
export const POINT_OF_SALE_RISK_TYPE_KEYS = ['chargebacks', 'reputational', 'legal'] as const;
export type PointOfSaleRiskTypeKey = (typeof POINT_OF_SALE_RISK_TYPE_KEYS)[number];

// ============================================================================
// Helper schemas used by transformed types
// ============================================================================

const PointOfSaleRiskTypeSchema = z.object({
  status: z.enum(['clear', 'alert']),
});

const PointOfSaleTagSchema = z.object({
  name: z.string(),
  level: z.enum(['positive', 'moderate', 'negative']),
});

const PointOfSaleMccSuggestionSchema = z.object({
  mcc: z.string(),
  description: z.string().nullish(),
  reasoning: z.string().nullish(),
});

const PointOfSaleLineOfBusinessSchema = z.object({
  lobFromWebsite: z.string().nullish(),
  mcc: z
    .object({
      primary: PointOfSaleMccSuggestionSchema.nullish(),
      alternatives: z.array(PointOfSaleMccSuggestionSchema).max(2).default([]),
    })
    .nullish(),
});

const PointOfSaleSynthesizerMerchantDetailsSchema = z.object({
  companyName: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  email: z.string().nullish(),
  country: z.string().nullish(),
  address: z.string().nullish(),
  website: z.string().nullish(),
});

const PointOfSaleSocialPageDataSchema = z.union([
  z.object({
    url: z.string().min(1),
    status: z.literal('detected'),
  }),
  z.object({
    url: z.union([z.null(), z.literal('')]),
    status: z.literal('missing'),
  }),
]);

const PointOfSaleSocialInfoSchema = z.object({
  facebook: PointOfSaleSocialPageDataSchema,
  instagram: PointOfSaleSocialPageDataSchema,
  tiktok: PointOfSaleSocialPageDataSchema,
  linkedin: PointOfSaleSocialPageDataSchema,
  twitter: PointOfSaleSocialPageDataSchema,
});

const PointOfSaleSocialDataSchema = z.union([
  z.object({
    url: z.string(),
    status: z.literal('detected'),
  }),
  z.object({
    url: z.null(),
    status: z.literal('missing'),
  }),
]);

export const PointOfSaleSocialMediaResponseSchema = z.record(
  z.enum(['facebook', 'instagram', 'tiktok', 'linkedin', 'twitter']),
  PointOfSaleSocialDataSchema,
);

export type PointOfSaleSocialMediaResponse = z.infer<typeof PointOfSaleSocialMediaResponseSchema>;

// ============================================================================
// Transformed/Normalized types for UI consumption
// These are the shapes returned by the API after backend transformation
// ============================================================================
export const NormalizedMerchantDetailsSchema = z
  .object({
    companyName: z.string().nullable(),
    alternativeCompanyNames: z.array(z.string()).nullable(),
    status: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    phoneNumbers: z.array(z.string()).nullish(),
    email: z.string().nullable(),
    emailAddresses: z.array(z.string()).nullish(),
    country: z.string().nullable(),
    address: z.string().nullable(),
    physicalAddresses: z.array(z.string()).nullish(),
    website: z.string().nullable(),
    associatedPersons: z.array(z.string()).nullable(),
  })
  .nullable();

export type NormalizedMerchantDetails = z.infer<typeof NormalizedMerchantDetailsSchema>;

export const NormalizedStakeholderSchema = z.object({
  name: z.string(),
  role: z.enum(STAKEHOLDER_ROLES),
  title: z.string().nullable(),
  nationality: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  cityOfBirth: z.string().nullable(),
  countryOfBirth: z.string().nullable(),
  residency: z
    .object({
      country: z.string().nullable(),
      state: z.string().nullable(),
      address: z.string().nullable(),
    })
    .nullable(),
});

export type NormalizedStakeholder = z.infer<typeof NormalizedStakeholderSchema>;

export const NormalizedProvidedDetailsSchema = z
  .object({
    companyName: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    country: z.string().nullable(),
    state: z.string().nullable(),
    address: z.string().nullable(),
    addresses: z.array(z.string()).nullable(),
    website: z.string().nullable(),
    emails: z.array(z.string()).nullable(),
    phoneNumbers: z.array(z.string()).nullable(),
    lineOfBusiness: z.string().nullable(),
    mcc: z.string().nullable(),
    dba: z.array(z.string()).nullable(),
    billingDescriptor: z.string().nullable(),
    icaBin: z.string().nullable(),
    icaBinName: z.string().nullable(),
    additionalInformation: z.string().nullable(),
    stakeholders: z.array(NormalizedStakeholderSchema).nullable(),
  })
  .nullable();

export type NormalizedProvidedDetails = z.infer<typeof NormalizedProvidedDetailsSchema>;

export const NormalizedMccSchema = z.object({
  code: z.string(),
  category: z.string().nullable(),
  reasoning: z.string().nullable(),
});

export type NormalizedMcc = z.infer<typeof NormalizedMccSchema>;

export const NormalizedOperationalProofSchema = z
  .object({
    lineOfBusiness: z.string().nullable(),
    lineOfBusinessReasoning: z.string().nullable(),
    mcc: NormalizedMccSchema.nullable(),
    alternatives: z.array(NormalizedMccSchema),
  })
  .nullable();

export type NormalizedOperationalProof = z.infer<typeof NormalizedOperationalProofSchema>;

export const NormalizedSynthesizerResultSchema = z
  .object({
    tags: z.array(PointOfSaleTagSchema),
    riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
    riskTypes: z.object({
      chargebacks: PointOfSaleRiskTypeSchema,
      reputational: PointOfSaleRiskTypeSchema,
      legal: PointOfSaleRiskTypeSchema,
    }),
    riskAnalysis: z.string(),
    references: z.string(),
    data: z
      .object({
        lineOfBusiness: PointOfSaleLineOfBusinessSchema.nullish(),
        merchantDetails: PointOfSaleSynthesizerMerchantDetailsSchema.nullish(),
        socialInfo: PointOfSaleSocialInfoSchema.nullish(),
      })
      .nullish(),
  })
  .nullable();

export type NormalizedSynthesizerResult = z.infer<typeof NormalizedSynthesizerResultSchema>;

export const StructuredTagWithLevelSchema = z.object({
  name: z.string(),
  level: z.enum(['positive', 'moderate', 'negative']),
  correlatesTo: z.string().nullable(),
});

export type StructuredTagWithLevel = z.infer<typeof StructuredTagWithLevelSchema>;

export const TransformedPointOfSaleReportDataSchema = z.object({
  matchReason: z.string().nullable(),
  merchantDetails: NormalizedMerchantDetailsSchema,
  providedDetails: NormalizedProvidedDetailsSchema,
  operationalProof: NormalizedOperationalProofSchema,
  ecosystem: z.array(EcosystemRecordSchema).nullable().optional(),
  socialMedia: PointOfSaleSocialMediaResponseSchema.nullable(),
  synthesizerResult: NormalizedSynthesizerResultSchema,
  structuredTags: z.array(StructuredTagWithLevelSchema),
});

export type TransformedPointOfSaleReportData = z.infer<
  typeof TransformedPointOfSaleReportDataSchema
>;

export const TransformedPointOfSaleReportSchema = z.object({
  id: z.string(),
  businessId: z.string().optional(),
  business: z
    .object({
      id: z.string(),
      correlationId: z.string().nullable(),
      companyName: z.string().nullable(),
    })
    .optional(),
  reportType: z.string().optional(),
  companyName: z.string().nullable().optional(),
  riskLevel: z.enum(['low', 'medium', 'high', 'critical']).nullable().optional(),
  status: PointOfSaleReportStatusEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
  data: TransformedPointOfSaleReportDataSchema,
  workflowStep: z.string().nullable(),
  publishedAt: z.string().nullable(),
  monitoringStatus: z.string().nullable().optional(),
  monitoringFrequency: z.string().nullable().optional(),
  nextMonitoringRunAt: z.string().nullable().optional(),
  ongoingMonitoringChanges: z.array(PointOfSaleOngoingMonitoringChangeSchema).nullable().optional(),
  ongoingMonitoringChangesSnippet: z.string().nullable().optional(),
  changesSummary: z.string().nullable().optional(),
  isAlert: z.boolean().nullable().optional(),
  comparedToReportId: z.string().nullable().optional(),
});

export type TransformedPointOfSaleReport = z.infer<typeof TransformedPointOfSaleReportSchema>;

export const TransformedPointOfSaleReportsResponseSchema = z.array(
  TransformedPointOfSaleReportSchema,
);

// ============================================================================
// Raw API Response Schemas
// These represent the structure returned by API endpoints as expected by consumers
// ============================================================================

const PointOfSaleReportCustomerSchema = z.object({
  id: z.string(),
  displayName: z.string(),
});

export const PointOfSaleReportSchema = TransformedPointOfSaleReportSchema.extend({
  customer: PointOfSaleReportCustomerSchema,
});

export type PointOfSaleReport = z.infer<typeof PointOfSaleReportSchema>;

export const PointOfSaleReportsResponseSchema = z.object({
  data: z.array(PointOfSaleReportSchema),
  totalItems: z.number(),
  totalPages: z.number().optional(),
});

export type PointOfSaleReportsResponse = z.infer<typeof PointOfSaleReportsResponseSchema>;
